import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import TestDataFormExampleModal from "./TestDataFormExampleModal";
import TestDataPageHelpModal from "./TestDataPageHelpModal";

const TEST_DATA_HELP_SECTIONS: {
  id: string;
  bold?: string;
  text: string;
  icon?: string;
}[] = [
  {
    id: "td-help-pencil",
    icon: "/assets/images/pencil-outline.svg",
    text: "Click the pencil icon to edit this row of data.",
  },
  {
    id: "td-help-trash",
    icon: "/assets/images/trash-outline.svg",
    text: "Click the trash icon to delete this row of data.",
  },
  {
    id: "td-help-add",
    text: 'Click on the "Add New Test Data" button to add each new row of data. Each row must be added separately.',
  },
  {
    id: "td-help-date",
    bold: "Date of Test:",
    text: "Date that data was collected (not the date it was added into to tracker)",
  },
  {
    id: "td-help-shift",
    bold: "Shift:",
    text: "If the test is conducted on different shifts on the same day, you can indicate it here.",
  },
  {
    id: "td-help-numerator",
    bold: "Numerator:",
    text: 'Many performance metrics are expressed as a percentage, calculated by dividing a numerator (e.g. number of women who received a mammogram) by a denominator (e.g. number of women who need a mammogram). Enter your data for the numerator or "top" number here.',
  },
  {
    id: "td-help-denominator",
    bold: "Denominator:",
    text: 'Many performance metrics are expressed as a percentage, calculated by dividing a numerator (e.g. number of women who received a mammogram) by a denominator (e.g. number of women who need a mammogram). Enter your data for the denominator or "bottom" number here. NOTE: If you are capturing a simple number rather than a percentage, enter "1" in this box.',
  },
  {
    id: "td-help-measure",
    bold: "Measure:",
    text: "UBT Tracker automatically calculates the percentage by dividing the numerator by the denominator and putting it here.",
  },
  {
    id: "td-help-notes",
    bold: "Notes:",
    text: "Use this field to enter any additional information about the test that you want to keep track of.",
  },
  {
    id: "td-help-outside",
    bold: "Outside Variable:",
    text: "This field allows you to track, group and compare your data into one or more categories that you identify and chart them. Consult an Improvement Advisor before using it.",
  },
];

interface TestDataHelpModalContentProps {
  onClose: () => void;
  onOpenFormExample: () => void;
  onOpenPageHelp: () => void;
}

const TestDataHelpModalContent: React.FC<TestDataHelpModalContentProps> = ({
  onClose,
  onOpenFormExample,
  onOpenPageHelp,
}) => {
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      data-testid="td-help-backdrop"
    >
      <button
        type="button"
        aria-label="Close help dialog"
        tabIndex={-1}
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0,0,0,0.4)",
          border: 0,
          padding: 0,
          cursor: "default",
        }}
      />
      <dialog
        open
        aria-labelledby="td-help-title"
        style={{
          position: "relative",
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          boxSizing: "border-box",
          width: "747px",
          height:'936px',
          maxHeight: "936px",
          display: "flex",
          flexDirection: "column",
          border: 0,
          padding: 0,
          margin: 0,
        }}
        data-testid="td-help-modal"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
          }}
        >
          <h2 className="h2"
          >
            Test Data Help
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            aria-label="Close help"
            onClick={onClose}
            style={{
              background: "transparent",
              border: 0,
              padding: 0,
              cursor: "pointer",
              fontSize: 20,
              lineHeight: 1,
              color: "#0074AD",
            }}
          >
            &#x2715;
          </button>
        </div>
        <div
          style={{
            overflowY: "auto",
            padding: "0px 24px 16px",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            fontSize: 16,
            color: "#374151",
          }}
        >
          {TEST_DATA_HELP_SECTIONS.map(({ id, bold, text, icon }) => (
            <p key={id} style={{ margin: 0, lineHeight: 1.5 }}>
              {icon != null && (
                <img
                  src={icon}
                  alt=""
                  aria-hidden="true"
                  width={16}
                  height={16}
                  style={{
                    display: "inline-block",
                    verticalAlign: "middle",
                    marginRight: 6,
                  }}
                />
              )}
              {bold != null && <strong>{bold} </strong>}
              {text}
            </p>
          ))}

          <hr className="mx-6 border-gray-300" />
          <div className="flex flex-col gap-3 px-6 py-4 text-sm text-gray-700 font-gotham5r!">
            <div>
              Click{" "}
              <button
                type="button"
                onClick={onOpenFormExample}
                className="text-kp-blue-dark! underline hover:text-[#005A8C] bg-transparent border-0 p-0 cursor-pointer"
                data-testid="td-help-form-example-link"
              >
                here
              </button>
              {" "}for an example of a completed form
            </div>
            <div>
              Click{" "}
              <button
                type="button"
                onClick={onOpenPageHelp}
                className="text-kp-blue-dark! underline hover:text-[#005A8C] bg-transparent border-0 p-0 cursor-pointer"
                data-testid="td-help-page-help-link"
              >
                here
              </button>
              {" "}for additional page help
            </div>
            <div>
              Click{" "}
              <a
                href="https://www.lmpartnership.org/path-to-performance/ubt-basics"
                target="_blank"
                rel="noopener noreferrer"
                className="text-kp-blue-dark! underline! hover:text-[#005A8C]"
                data-testid="td-help-lmp-link"
              >
                here
              </a>
              {" "}for the National LMP web site
            </div>
          </div>
        </div>
  
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "16px 24px",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="button -inverted flex! items-center!"
          >
            Close
          </button>
        </div>
      </dialog>
    </div>
  );
};

const TestDataHelpModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFormExampleOpen, setIsFormExampleOpen] = useState(false);
  const [isPageHelpOpen, setIsPageHelpOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const openFormExample = useCallback(() => {
    setIsOpen(false);
    setIsFormExampleOpen(true);
  }, []);
  const openPageHelp = useCallback(() => {
    setIsOpen(false);
    setIsPageHelpOpen(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, close]);

  return (
    <>
      <button
        type="button"
        aria-label="Help"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={open}
        className="inline-flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer"
        data-testid="td-help-trigger"
      >
        <span className="font-semibold" style={{ color: "#0074AD" }}>
          Help
        </span>
        <img
          src="/assets/images/info-circle-small-outline.svg"
          alt=""
          aria-hidden="true"
          width={20}
          height={20}
        />
      </button>
      {isOpen &&
        ReactDOM.createPortal(
          <TestDataHelpModalContent
            onClose={close}
            onOpenFormExample={openFormExample}
            onOpenPageHelp={openPageHelp}
          />,
          document.body
        )}
      <TestDataFormExampleModal
        isOpen={isFormExampleOpen}
        onClose={() => setIsFormExampleOpen(false)}
      />
      <TestDataPageHelpModal
        isOpen={isPageHelpOpen}
        onClose={() => setIsPageHelpOpen(false)}
      />
    </>
  );
};

export default TestDataHelpModal;
